import type { RangePeriod } from "@finances/shared";
import { defaultMonth } from "./format.js";

/**
 * The month or range last chosen anywhere in the app, so picking May — or
 * "Last 3 months" — on one screen and moving to another shows the same thing
 * there too, rather than each screen falling straight back to last month.
 * Same shape as `theme.tsx`'s storage: read defensively, write best-effort,
 * no error if storage is unavailable.
 *
 * A month and a range are kept in separate keys but are mutually exclusive —
 * choosing one clears the other, so the two cannot disagree about which was
 * chosen more recently.
 */

/**
 * A single month, or one of the picker's ranges — exactly one, no default.
 * Both members carry both keys (one of them always `undefined`) so a caller
 * can read `scope.month` without narrowing first.
 */
export type Scope =
  { month: string; period?: undefined } | { month?: undefined; period: RangePeriod };

const STORAGE_KEY = "lastMonth";
const PERIOD_KEY = "lastPeriod";
const MONTH_PATTERN = /^\d{4}-\d{2}$/;
const RANGE_PERIODS: readonly RangePeriod[] = [
  "last-3-months",
  "last-6-months",
  "last-12-months",
  "all-time",
];

export function getLastMonth(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && MONTH_PATTERN.test(stored)) return stored;
  } catch {
    // Storage unavailable. Callers fall back to their own default.
  }
  return null;
}

export function setLastMonth(month: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, month);
    localStorage.removeItem(PERIOD_KEY);
  } catch {
    // Preference is not persisted; the session still honours it.
  }
}

export function getLastPeriod(): RangePeriod | null {
  try {
    const stored = localStorage.getItem(PERIOD_KEY);
    if (stored && (RANGE_PERIODS as readonly string[]).includes(stored)) {
      return stored as RangePeriod;
    }
  } catch {
    // Storage unavailable. Callers fall back to their own default.
  }
  return null;
}

export function setLastPeriod(period: RangePeriod): void {
  try {
    localStorage.setItem(PERIOD_KEY, period);
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Preference is not persisted; the session still honours it.
  }
}

/** Whichever of the two was chosen more recently, if either. */
export function getLastScope(): Scope | null {
  const period = getLastPeriod();
  if (period) return { period };
  const month = getLastMonth();
  return month ? { month } : null;
}

/**
 * What a screen that always needs a scope — Dashboard, Categories — should
 * show: whatever the URL names explicitly, else whatever was last chosen
 * anywhere else, else last month.
 */
export function resolveScope(search: { month?: string; period?: RangePeriod }): Scope {
  if (search.month) return { month: search.month };
  if (search.period) return { period: search.period };
  return getLastScope() ?? { month: defaultMonth() };
}

/**
 * A scope, as a Transactions search. Transactions already has its own "every
 * month" — an absent month and an absent period — so `all-time` becomes that
 * rather than a range value Transactions does not offer.
 */
export function toTransactionsSearch(scope: Scope): {
  month?: string;
  period?: "last-3-months" | "last-6-months" | "last-12-months";
} {
  if ("month" in scope && scope.month) return { month: scope.month };
  if (scope.period === "all-time") return {};
  return { period: scope.period };
}

const RANGE_LABELS: Record<RangePeriod, string> = {
  "last-3-months": "Last 3 months",
  "last-6-months": "Last 6 months",
  "last-12-months": "Last 12 months",
  "all-time": "All time",
};

/** The scope, in words, before the server has resolved it to real dates. */
export function scopeLabel(scope: Scope, format: (month: string) => string): string {
  if ("period" in scope && scope.period) return RANGE_LABELS[scope.period];
  return format(scope.month as string);
}
